/**
 * CreateEventDetails — Step: Format & Visibility (method, visibility, location, host).
 * Verbatim port of the prototype's CreateEventDetails + EventLocationSection.
 */

import { useState, useEffect, useRef, type ChangeEvent } from "react";
import { ChoiceTile, Dropdown, Icon, Input, SegmentedControl, T } from "@v0y4ge/jrni-ui";
import { suggestAddresses, resolveAddress, validateAddress } from "@v0y4ge/api-client";
import { studioRequestOptions } from "../../../lib/studio-config";
import { CreationShell } from "./CreationShell";
import { CreationStore, useCreationConfig, useCreationState, normalizeVirtualProvider } from "./store";
import { FieldError, FormSection, WizardCanvas } from "./wizard-utils";
import { checkUrl, UrlField } from "./url-field";
import { ProviderLogo } from "../../settings/ProviderLogo";
import { useStaff } from "../../staff/useStaff";
import { useLocations } from "../../locations/useLocations";
import { useT } from "../../../lib/i18n";

// ── Helpers ──────────────────────────────────────────────────────────────────
const labelSt = { fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 } as const;

// ── EventFormatToggle ─────────────────────────────────────────────────────────
function EventFormatToggle({ cfg }: { cfg: ReturnType<typeof useCreationConfig> }) {
  const t = useT();
  const m = cfg.method || { inPerson: true, virtual: false };
  const current = m.inPerson && m.virtual ? "hybrid" : m.virtual ? "virtual" : "inperson";
  const set = (v: string) => {
    const method = v === "hybrid" ? { inPerson: true, virtual: true } : v === "virtual" ? { inPerson: false, virtual: true } : { inPerson: true, virtual: false };
    CreationStore.setMethod(method);
  };
  const OPTS = [
    { v: "inperson", l: "In person", i: <Icon.loc size={14} />, d: "A physical venue" },
    { v: "virtual", l: "Virtual", i: <Icon.video size={14} />, d: "Online only" },
    { v: "hybrid", l: "Hybrid", i: <Icon.globe size={14} />, d: "Venue + online" },
  ];
  return (
    <div>
      <label style={{ ...labelSt }}>{t("events.creationCreateEventDetails.format", "Format")}<span style={{ color: T.red1, marginLeft: 3 }}>*</span></label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
        {OPTS.map((o) => {
          const on = current === o.v;
          return (
            <ChoiceTile key={o.v} leading={o.i} label={o.l} sublabel={o.d} selected={on} onSelect={() => set(o.v)} />
          );
        })}
      </div>
    </div>
  );
}

// ── VirtualProviderPicker ────────────────────────────────────────────────────
// ids are the ratified EVENT_VIRTUAL_PROVIDER_IDS (kit e670c958); anything else 400s.
// `logos` = Iconify candidates tried in order for the real brand mark. Webex,
// GoTo and Zoom's current mark do NOT exist in the `logos` set (every name 404s,
// which is why they fell back to letter tiles), so those are set-qualified
// `simple-icons/…` glyphs tinted to the brand color
// (ProviderLogo falls back to a brand-letter tile if none resolve) — the old
// generic video glyph on a tinted square made every provider look alike.
// `hosts` drives the soft "doesn't look like a <provider> link" hint only.
const VIRTUAL_PROVIDERS = [ // ui-config
  { id: "zoom", name: "Zoom", blurb: "Auto-create meeting", hue: "#0B5CFF", logos: ["simple-icons/zoom?color=%230B5CFF", "logos/zoom"], hosts: ["zoom.us", "zoomgov.com"], example: "https://acme.zoom.us/j/123456789" },
  { id: "webex", name: "Cisco Webex", blurb: "Auto-create meeting", hue: "#00BCEB", logos: ["simple-icons/webex?color=%2300BCEB", "simple-icons/cisco?color=%231BA0D7"], hosts: ["webex.com"], example: "https://acme.webex.com/meet/team" },
  { id: "goto", name: "GoTo Meeting", blurb: "Auto-create meeting", hue: "#F68D2E", logos: ["simple-icons/gotomeeting?color=%23F68D2E"], hosts: ["goto.com", "gotomeeting.com", "gotomeet.me"], example: "https://meet.goto.com/123456789" },
  { id: "gmeet", name: "Google Meet", blurb: "Auto-generate link", hue: "#00897B", logos: ["google-meet"], hosts: ["meet.google.com"], example: "https://meet.google.com/abc-defg-hij" },
  { id: "msteams", name: "Microsoft Teams", blurb: "Auto-create meeting", hue: "#6264A7", logos: ["microsoft-teams"], hosts: ["teams.microsoft.com", "teams.live.com"], example: "https://teams.microsoft.com/l/meetup-join/…" },
  { id: "custom", name: "Custom link", blurb: "Paste any URL", hue: T.n2, logos: [], hosts: [], example: "https://example.com/join" },
];

function VirtualProviderPicker({ cfg }: { cfg: ReturnType<typeof useCreationConfig> }) {
  const t = useT();
  const st = useCreationState();
  const sel = normalizeVirtualProvider((cfg as { virtualProvider?: string }).virtualProvider);
  const provider = VIRTUAL_PROVIDERS.find((p) => p.id === sel);
  const virtualLink = (cfg as { virtualLink?: string }).virtualLink || "";
  // F-87: on a saved event with an active real provider, the join link is the
  // provider meeting created server-side (studio_events.meeting_link) — render
  // it read-only; PUT /mode on save re-syncs it. Only `custom` stays editable.
  const linkLocked = !!st._editingId && !!virtualLink && !!sel && sel !== "custom";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <label style={{ ...labelSt }}>{t("events.creationCreateEventDetails.meetingProvider", "Meeting provider")}</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
          {VIRTUAL_PROVIDERS.map((p) => {
            const on = sel === p.id;
            return (
              <ChoiceTile
                key={p.id}
                leading={p.logos.length ? (
                  <ProviderLogo id={p.id} name={p.name} brand={p.hue} size={26} radius={6} logoCandidates={p.logos} />
                ) : (
                  <span style={{ width: 26, height: 26, borderRadius: 6, background: T.n6, border: `1px solid ${T.n5}`, color: T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.link size={13} /></span>
                )}
                label={p.name}
                sublabel={p.blurb}
                selected={on}
                onSelect={() => CreationStore.setField("virtualProvider", p.id)}
                showCheck={false}
              />
            );
          })}
        </div>
      </div>
      {sel ? (
        linkLocked ? (
          <div>
            <label style={{ ...labelSt }}>{provider?.name || ""} {t("events.creationCreateEventDetails.joinLink", "join link")}</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8, height: 40, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.n6 }}>
              <span style={{ color: T.soft, display: "inline-flex", flexShrink: 0 }}><Icon.lock size={12} /></span>
              <span style={{ flex: 1, fontSize: 13, color: T.n2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{virtualLink}</span>
            </div>
            <div style={{ fontSize: 11, color: T.soft, marginTop: 4 }}>{t("events.creationCreateEventDetails.managedBy", "Managed by")}{" "}{provider?.name} {t("events.creationCreateEventDetails.theMeetingReSyncsWhenYouSaveThisEvent", "— the meeting re-syncs when you save this event.")}</div>
          </div>
        ) : (
          <div>
            <label style={{ ...labelSt }} htmlFor="ev-virtual-link">{provider?.id === "custom" ? t('events.creationCreateEventDetails.text5') : /* TODO(i18n): replace template-literal with t('events.creationCreateEventDetails.text6', { ... }) */`${provider?.name || ""} link`}<span style={{ color: T.soft, fontWeight: 400 }}> {t("events.creationCreateEventDetails.optional", "· optional")}</span></label>
            <UrlField
              id="ev-virtual-link"
              value={virtualLink}
              onChange={(v) => CreationStore.setField("virtualLink", v)}
              placeholder={provider?.example || t("events.creationCreateEventDetails.https", "https://…")}
              example={provider?.example}
              expectHosts={provider?.id === "custom" ? undefined : provider?.hosts}
              expectName={provider?.name}
            />
          </div>
        )
      ) : null}
    </div>
  );
}

// ── InPersonAddress ───────────────────────────────────────────────────────────
// Two modes: pick a saved location (from the Locations rail) or enter a new
// address stored on this event. Location data is sourced from useLocations.
// ── AddressAutocomplete ────────────────────────────────────────────────────────
// Debounced geo.suggestAddresses → resolveAddress on pick. Degrades to plain free
// text entry (no dropdown, no validated flag) if the tenant has no geo provider
// configured (409 GEO_PROVIDER_NOT_CONFIGURED) or the request otherwise fails —
// never blocks typing, never fakes a validated result. REQ-010: free-typed
// addresses (no dropdown pick) are validated on blur via geo.validateAddress —
// `valid:false` is a well-formed-but-unmatched 200, not an error, so it only
// ever informs (a soft "couldn't verify" note), never blocks saving.
type GeoSuggestion = { id: string; label: string; primary?: string; secondary?: string; address?: Record<string, string>; lat?: number; lng?: number; tz?: string };
type AddressValidation = { valid: boolean; label?: string; address?: Record<string, string>; lat?: number; lng?: number; tz?: string; reason?: string };
function AddressAutocomplete({ value, onChangeText, onSelect, onValidate }: { value: string; onChangeText: (v: string) => void; onSelect: (s: GeoSuggestion) => void; onValidate?: (v: AddressValidation) => void }) {
  const t = useT();
  const [suggestions, setSuggestions] = useState<GeoSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verify, setVerify] = useState<"idle" | "checking" | "valid" | "invalid">("idle");
  const [verifyReason, setVerifyReason] = useState<string | undefined>(undefined);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null); // UIR-28: cleared on unmount
  const pickedRef = useRef(false);
  const geoOpts = studioRequestOptions("geo") as { baseUrl: string; surface?: string };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); if (blurCloseRef.current) clearTimeout(blurCloseRef.current); }, []);

  const onType = (v: string) => {
    onChangeText(v);
    pickedRef.current = false;
    setVerify("idle");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (v.trim().length < 4) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await suggestAddresses({ q: v.trim(), limit: 5 }, geoOpts);
        setSuggestions((res.suggestions ?? []) as GeoSuggestion[]);
        setOpen(true);
      } catch (err) {
        console.warn("suggestAddresses unavailable:", err);
        setSuggestions([]); setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const pick = async (s: GeoSuggestion) => {
    setOpen(false);
    pickedRef.current = true;
    setVerify("valid");
    try {
      const resolved = await resolveAddress(s.id, geoOpts);
      onSelect(resolved as GeoSuggestion);
    } catch (err) {
      console.warn("resolveAddress failed, using suggestion as-is:", err);
      onSelect(s);
    }
  };

  const onBlurValidate = () => {
    if (pickedRef.current || !onValidate) return;
    const q = value.trim();
    if (q.length < 4) return;
    setVerify("checking");
    validateAddress({ address: q }, geoOpts).then((res) => {
      if (res.valid) {
        setVerify("valid");
        onValidate({ valid: true, label: res.label, address: res.normalized as unknown as Record<string, string> | undefined, lat: res.lat, lng: res.lng, tz: res.tz });
      } else {
        setVerify("invalid");
        setVerifyReason(res.reason);
        onValidate({ valid: false, reason: res.reason });
      }
    }).catch((err) => {
      console.warn("validateAddress unavailable:", err);
      setVerify("idle");
    });
  };

  return (
    <div style={{ position: "relative" }}>
      <Input id="venue-addr" value={value} onChange={(e: ChangeEvent<HTMLInputElement>) => onType(e.target.value)}
        onFocus={() => { if (suggestions.length) setOpen(true); }}
        onBlur={() => { if (blurCloseRef.current) clearTimeout(blurCloseRef.current); blurCloseRef.current = setTimeout(() => setOpen(false), 150); onBlurValidate(); }}
        placeholder={t("events.creationCreateEventDetails.streetCityRegionPostalCode", "Street, city, region, postal code")} autoComplete="off" />
      {loading ? <div style={{ position: "absolute", right: 10, top: 10, fontSize: 11, color: T.soft }}>{t("events.creationCreateEventDetails.searching", "Searching…")}</div> : null}
      {verify === "checking" ? <div style={{ marginTop: 4, fontSize: 11.5, color: T.soft }}>{t("events.creationCreateEventDetails.verifyingAddress", "Verifying address…")}</div> : null}
      {verify === "valid" ? <div style={{ marginTop: 4, fontSize: 11.5, color: T.green1, display: "flex", alignItems: "center", gap: 4 }}><Icon.check size={11} />{t("events.creationCreateEventDetails.verified", "Verified")}</div> : null}
      {verify === "invalid" ? <div style={{ marginTop: 4, fontSize: 11.5, color: T.soft }}>{t("events.creationCreateEventDetails.couldnTVerifyThisAddress", "Couldn't verify this address")}{" "}{verifyReason ? t('events.creationCreateEventDetails.text7', { verifyReason }) : ""}{t("events.creationCreateEventDetails.butItSSavedAsEntered", ", but it's saved as entered.")}</div> : null}
      {open && suggestions.length > 0 ? (
        <div style={{ position: "absolute", zIndex: 20, top: "100%", left: 0, right: 0, marginTop: 4, background: T.white, border: `1px solid ${T.n4}`, borderRadius: 8, boxShadow: T.shadowMd, overflow: "hidden" }}>
          {suggestions.map((s) => (
            <div key={s.id} onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              style={{ padding: "9px 12px", cursor: "pointer", fontSize: 13, color: T.n1, borderBottom: `1px solid ${T.n6}` }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.n6; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}>
              <div style={{ fontWeight: 600 }}>{s.primary || s.label}</div>
              {s.secondary ? <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1 }}>{s.secondary}</div> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function InPersonAddress({ cfg }: { cfg: ReturnType<typeof useCreationConfig> }) {
  const t = useT();
  const c = cfg as { venueName?: string; address?: string; locationId?: string; locationMode?: "saved" | "custom" };
  const { locations, loading } = useLocations();
  const mode: "saved" | "custom" = c.locationMode || (c.locationId ? "saved" : (c.address || c.venueName ? "custom" : "saved"));
  const setMode = (m: "saved" | "custom") => {
    CreationStore.setField("locationMode", m);
    if (m === "custom") CreationStore.setField("locationId", "");
  };
  const pickSaved = (id: string) => {
    const loc = locations.find((l) => l.id === id);
    CreationStore.setField("locationId", id);
    if (loc) {
      CreationStore.setField("venueName", loc.name);
      CreationStore.setField("address", loc.address);
      CreationStore.setField("location", loc.name);
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SegmentedControl<"saved" | "custom">
        options={[{ value: "saved", label: "Saved location" }, { value: "custom", label: "New address" }]}
        value={mode}
        onChange={setMode}
        size="sm"
      />


      {mode === "saved" ? (
        <div>
          <label style={{ ...labelSt }}>{t("events.creationCreateEventDetails.location", "Location")}</label>
          {loading ? <div style={{ fontSize: 13, color: T.soft }}>{t("events.creationCreateEventDetails.loadingLocations", "Loading locations…")}</div> : locations.length === 0 ? (
            <div style={{ fontSize: 13, color: T.soft, padding: "8px 0" }}>{t("events.creationCreateEventDetails.noSavedLocationsYetAddOneUnder", "No saved locations yet. Add one under")}{" "}<strong style={{ color: T.n1 }}>{t("events.creationCreateEventDetails.locations", "Locations")}</strong>{t("events.creationCreateEventDetails.orSwitchToNewAddress", ", or switch to New address.")}</div>
          ) : (
            <>
              <Dropdown
                options={locations.map((l) => ({ value: l.id, label: l.name }))}
                value={c.locationId || null}
                onChange={(v) => pickSaved(v)}
                placeholder={t("events.creationCreateEventDetails.selectALocation", "Select a location…")}
                aria-label={t("events.creationCreateEventDetails.location", "Location")}
                testid="ev-loc"
                style={{ gap: 0 }}
              />
              {c.locationId ? (() => { const loc = locations.find((l) => l.id === c.locationId); return loc ? <div style={{ marginTop: 8, padding: "10px 12px", background: T.n6, borderRadius: 8, fontSize: 12.5, color: T.n2, display: "flex", alignItems: "center", gap: 8 }}><Icon.loc size={13} style={{ color: T.p1, flexShrink: 0 }} /><span>{loc.address}{loc.tz ? ` · ${loc.tz}` : ""}</span></div> : null; })() : null}
            </>
          )}
        </div>
      ) : (
        <>
          <div>
            <label style={{ ...labelSt }} htmlFor="venue-name">{t("events.creationCreateEventDetails.venueName", "Venue name")}</label>
            <Input id="venue-name" value={c.venueName || ""} onChange={(e: ChangeEvent<HTMLInputElement>) => { CreationStore.setField("venueName", e.target.value); CreationStore.setField("location", e.target.value); }} placeholder={t("events.creationCreateEventDetails.eGWestsideBranchCommunityRoom", "e.g. Westside Branch · Community Room")} />
          </div>
          <div style={{ position: "relative" }}>
            <label style={{ ...labelSt }} htmlFor="venue-addr">{t("events.creationCreateEventDetails.address", "Address")}</label>
            <AddressAutocomplete
              value={c.address || ""}
              onChangeText={(v) => { CreationStore.setField("address", v); CreationStore.setField("geo", null); }}
              onSelect={(resolved) => {
                CreationStore.setField("address", resolved.label);
                CreationStore.setField("geo", { placeId: resolved.id, address: resolved.address ?? null, lat: resolved.lat, lng: resolved.lng, tz: resolved.tz, validated: true });
                // #749 residual-3 (CD-BOUNCE-drop19): the LOCATION summary rail reads
                // `location`; a picked address must set it too, or a valid pick still
                // reads "Not set". Prefer the venue name when given, else the label.
                CreationStore.setField("location", c.venueName || resolved.label);
              }}
              onValidate={(v) => {
                // REQ-010: a free-typed address (no dropdown pick) gets its own geo
                // record once blur-validated — same shape/consumer as the pick path,
                // `validated` just reflects whether the provider matched it.
                CreationStore.setField("geo", v.valid ? { placeId: null, address: v.address ?? null, lat: v.lat, lng: v.lng, tz: v.tz, validated: true } : { validated: false });
                if (v.valid) CreationStore.setField("location", c.venueName || v.label || c.address);
              }}
            />
          </div>
          <div style={{ fontSize: 12, color: T.soft }}>{t("events.creationCreateEventDetails.thisAddressIsSavedWithThisEventOnlyToReuseItAdd", "This address is saved with this event only. To reuse it, add it under")}{" "}<strong style={{ color: T.n1 }}>{t("events.creationCreateEventDetails.locations", "Locations")}</strong>.</div>
        </>
      )}
    </div>
  );
}

// ── EventLocationSection ──────────────────────────────────────────────────────
function EventLocationSection({ cfg }: { cfg: ReturnType<typeof useCreationConfig> }) {
  const t = useT();
  const m = cfg.method || { inPerson: false, virtual: false };
  const showInPerson = !!m.inPerson; const showVirtual = !!m.virtual;
  const subtitle = showInPerson && showVirtual ? "Hybrid — set the physical venue and the online platform." : showVirtual ? "Virtual — pick the meeting provider." : showInPerson ? "In person — enter the venue details." : "Pick a format above to set the location.";
  return (
    <FormSection title={t("events.creationCreateEventDetails.location", "Location")} subtitle={subtitle}>
      {showInPerson ? <InPersonAddress cfg={cfg} /> : null}
      {showInPerson && showVirtual ? <div style={{ height: 1, background: T.border, margin: "2px 0" }} /> : null}
      {showVirtual ? <VirtualProviderPicker cfg={cfg} /> : null}
      {!showInPerson && !showVirtual ? <div style={{ fontSize: 13, color: T.soft }}>{t("events.creationCreateEventDetails.pickAFormatAboveToSetTheLocation", "Pick a format above to set the location.")}</div> : null}
      <FieldError path={["location", "meetingLink"]} />
    </FormSection>
  );
}

// ── HostPicker (from Staff) ───────────────────────────────────────────────────
function HostPicker({ cfg }: { cfg: ReturnType<typeof useCreationConfig> }) {
  const t = useT();
  const host = (cfg as { host?: string }).host || "";
  const { members, loading } = useStaff();
  const active = members.filter((m) => (m.status as string) !== "archived" && m.status !== "inactive");
  const label = (m: { firstName: string; lastName: string; title?: string }) => `${m.firstName} ${m.lastName}`.trim() + (m.title ? ` · ${m.title}` : "");
  const placeholder = loading ? t('events.creationCreateEventDetails.text9') : active.length ? t('events.creationCreateEventDetails.text10') : t('events.creationCreateEventDetails.text11');
  return (
    <div>
      <label style={{ ...labelSt }}>{t("events.creationCreateEventDetails.host", "Host")}<span style={{ color: T.soft, fontWeight: 400 }}> {t("events.creationCreateEventDetails.optional", "· optional")}</span></label>
      <Dropdown
        options={active.map((m) => { const l = label(m); return { value: l, label: l }; })}
        value={host || null}
        onChange={(v) => CreationStore.setField("host", v)}
        placeholder={placeholder}
        disabled={loading || active.length === 0}
        aria-label={t("events.creationCreateEventDetails.host", "Host")}
        testid="ev-host"
        style={{ gap: 0 }}
      />
    </div>
  );
}

// ── CreateEventDetails ────────────────────────────────────────────────────────
export default function CreateEventDetails() {
  const t = useT();
  const cfg = useCreationConfig();
  const visibility = cfg.visibility;
  const method = cfg.method;
  const visibilityOptions = ["Public · Anyone with link", "Invite · RSVP", "Public & Invite", "Private · Internal"];
  const c2 = cfg as { location?: string; locationId?: string; venueName?: string; address?: string; virtualProvider?: string; virtualLink?: string };
  const missing: string[] = [];
  if (!visibility) missing.push("visibility");
  if (!(method && (method.inPerson || method.virtual))) missing.push("format");
  // Location is required by format: a physical venue for in-person/hybrid, a
  // meeting provider for virtual/hybrid. Previously unchecked — Continue was
  // enabled with Location "Not set" (operator report, 2026-07-13).
  if (method?.inPerson && !String(c2.location || c2.locationId || c2.venueName || c2.address || "").trim()) missing.push("location");
  if (method?.virtual && !String(c2.virtualProvider || "").trim()) missing.push("meeting provider");
  // A typed-but-malformed join link blocks Continue: it's optional, but a bad one
  // ships a dead link into every confirmation email (demo report, 2026-07-30).
  if (method?.virtual && checkUrl(String(c2.virtualLink || "")).state === "invalid") missing.push("a valid meeting link");
  const nextDisabled = missing.length > 0;
  const nextDisabledReason = nextDisabled ? `Missing: ${missing.join(", ")}` : "";

  return (
    <CreationShell step={1} nextDisabled={nextDisabled} nextDisabledReason={nextDisabledReason}>
      <WizardCanvas width="wide" cols="stack">
        <FormSection title={t("events.creationCreateEventDetails.formatVisibility", "Format & visibility")} subtitle={t("events.creationCreateEventDetails.howTheEventIsDeliveredAndWhoCanFindOrRegisterFor", "How the event is delivered, and who can find or register for it.")}>
          {/* Fields were flush against each other (demo report, 2026-07-30) — FormSection
              lays its children out flush by design, so the step owns the field rhythm. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <EventFormatToggle cfg={cfg} />
              <FieldError path="method" />
            </div>
            <div>
              <label style={{ ...labelSt }}>{t("events.creationCreateEventDetails.visibility", "Visibility")}<span style={{ color: T.red1, marginLeft: 3 }}>*</span></label>
              <Dropdown
                options={visibilityOptions.map((v) => ({ value: v, label: v }))}
                value={visibility || null}
                onChange={(v) => CreationStore.setVisibility(v)}
                placeholder={t("events.creationCreateEventDetails.chooseVisibility", "Choose visibility…")}
                aria-label={t("events.creationCreateEventDetails.visibility", "Visibility")}
                testid="ev-vis"
                style={{ gap: 0 }}
              />
              <FieldError path="visibility" />
            </div>
            <HostPicker cfg={cfg} />
          </div>
        </FormSection>
        <EventLocationSection cfg={cfg} />
      </WizardCanvas>
    </CreationShell>
  );
}
